import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Loader2, MessageSquareText, Search } from "lucide-react";
import { ReviewCard } from "@/components/reviews/ReviewList";
import { PhotoLightbox } from "@/components/reviews/PhotoLightbox";
import { ReviewAdminDialogs } from "@/components/reviews/ReviewAdminDialogs";
import { useAdminReviews } from "@/hooks/useReviews";
import { useReviewAdmin } from "@/hooks/useReviewAdmin";

const TABS = [
  { value: "pending", label: "Pending" },
  { value: "hidden", label: "Hidden" },
  { value: "published", label: "Published" },
  { value: "all", label: "All" },
];
const PAGE = 10;
const EMPTY = {
  pending: ["Nothing awaiting approval", "New reviews publish instantly — anything you hide or hold will appear here."],
  hidden: ["No hidden reviews", "Reviews you hide from the storefront will be listed here so you can restore them."],
  published: ["No published reviews yet", "Live customer reviews will appear here once they start coming in."],
  all: ["No reviews yet", "Customer reviews from every product will be listed here."],
};

function ProductStrip({ product }) {
  return (
    <Link to={`/product/${product.handle}`} className="group mb-4 flex items-center gap-3 border-b border-border pb-3" data-testid="moderation-product-link">
      {product.image ? (
        <img src={product.image} alt="" className="h-10 w-10 object-cover" />
      ) : (
        <span className="grid h-10 w-10 place-items-center bg-cream text-[10px] text-muted-foreground">—</span>
      )}
      <span className="flex-1 truncate font-serif text-base text-ink group-hover:text-gold">{product.title}</span>
      <ArrowUpRight size={14} className="text-muted-foreground group-hover:text-gold" />
    </Link>
  );
}

function StatusTabs({ status, counts, onChange }) {
  return (
    <div className="mt-6 flex flex-wrap gap-2" role="tablist" data-testid="moderation-status-tabs">
      {TABS.map((t) => {
        const active = status === t.value;
        return (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(t.value)}
            data-testid={`moderation-tab-${t.value}`}
            className={`inline-flex items-center gap-2 border px-4 py-2 text-[11px] uppercase tracking-widest transition-colors ${active ? "border-ink bg-ink text-ivory" : "border-border text-muted-foreground hover:border-gold hover:text-gold"}`}
          >
            {t.label}
            <span className={`min-w-[1.25rem] px-1.5 py-0.5 text-center text-[10px] ${active ? "bg-gold text-ink" : "bg-cream text-ink"}`} data-testid={`moderation-count-${t.value}`}>{counts[t.value] ?? "–"}</span>
          </button>
        );
      })}
    </div>
  );
}

function ModerationList({ status, items, isLoading, admin, onPhoto }) {
  if (isLoading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>;
  if (items.length === 0) {
    const [title, hint] = EMPTY[status];
    return (
      <div className="mt-6 border border-dashed border-border py-16 text-center" data-testid="moderation-empty">
        <p className="font-serif text-2xl text-ink">{title}</p>
        <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">{hint}</p>
      </div>
    );
  }
  return (
    <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-2">
      {items.map((r) => <ReviewCard key={r.id} review={r} admin={admin} onPhoto={onPhoto} header={<ProductStrip product={r.product} />} />)}
    </div>
  );
}

export function ReviewModerationPanel({ defaultStatus = "pending" }) {
  const [status, setStatus] = useState(defaultStatus);
  const [q, setQ] = useState("");
  const [limit, setLimit] = useState(PAGE);
  const [lightbox, setLightbox] = useState(null);
  const { data, isLoading, isFetching } = useAdminReviews({ status, q, limit });
  const admin = useReviewAdmin();

  return (
    <div data-testid="review-moderation-panel">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <MessageSquareText size={20} className="mt-0.5 text-gold" />
          <div>
            <h2 className="text-luxe text-[11px] text-ink">Review Moderation</h2>
            <p className="mt-1 text-xs text-muted-foreground">Approve, hide, reply to or delete customer reviews across every product. Replies are published as ÉLORA JEWELS.</p>
          </div>
        </div>
        <label className="relative block w-full sm:w-72">
          <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={q}
            onChange={(e) => { setQ(e.target.value); setLimit(PAGE); }}
            placeholder="Search name, text or product…"
            data-testid="moderation-search-input"
            className="w-full border border-border bg-transparent py-2.5 pl-9 pr-3 text-sm focus:border-gold focus:outline-none"
          />
        </label>
      </div>

      <StatusTabs status={status} counts={data?.counts || {}} onChange={(s) => { setStatus(s); setLimit(PAGE); }} />
      <ModerationList status={status} items={data?.items || []} isLoading={isLoading} admin={admin} onPhoto={(photos, index) => setLightbox({ photos, index })} />

      {data?.has_more && (
        <div className="mt-8 text-center">
          <button type="button" onClick={() => setLimit((l) => l + PAGE)} disabled={isFetching} data-testid="moderation-load-more" className="border border-border px-8 py-3.5 text-[11px] uppercase tracking-[0.18em] text-ink transition-colors hover:border-gold hover:text-gold disabled:opacity-50">
            {isFetching ? "Loading…" : "Load More"}
          </button>
        </div>
      )}

      <PhotoLightbox photos={lightbox?.photos || []} index={lightbox?.index || 0} open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)} />
      <ReviewAdminDialogs admin={admin} />
    </div>
  );
}
